import { useEffect, useRef, useState } from "react";
import BranchSettingsService, {
  SKIP_PRODUCTION_LIST_TYPE,
  SKIP_PRODUCTION_SCOPE,
  SkipProductionCustomer,
} from "../services/branch-settings-service";
import { CustomerService, Customer } from "../../customer/services/customer-service";
import { isSuccess } from "../../../models/response/base-response";
import { showToast } from "../../../components/custom/custom-toast";

interface Props {
  branchId: number;
  scope: number;
  disabled?: boolean;
  onScopeChange: (scope: number) => void;
}

/**
 * Phạm vi "Bỏ qua quét sọt/máy giặt" — chỉ hiện khi cờ chi nhánh đang BẬT.
 * Mọi khách (mặc định) / mọi khách trừ danh sách loại trừ / chỉ khách chỉ định — 2 danh sách riêng, gắn theo
 * chi nhánh đang giữ đồ.
 */
const SkipProductionScope: React.FC<Props> = ({ branchId, scope, disabled, onScopeChange }) => {
  const [customers, setCustomers] = useState<SkipProductionCustomer[]>([]);
  const [keyword, setKeyword] = useState("");
  const [results, setResults] = useState<Customer[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);

  const { fetch: listSelected, loading: loadingList } = BranchSettingsService.skipProductionCustomers();
  const { fetch: addCustomers } = BranchSettingsService.addSkipProductionCustomers();
  const { fetch: removeCustomers } = BranchSettingsService.removeSkipProductionCustomers();
  const { fetch: searchCustomers } = CustomerService.list();

  const listRef = useRef(listSelected);
  const addRef = useRef(addCustomers);
  const removeRef = useRef(removeCustomers);
  const searchRef = useRef(searchCustomers);
  useEffect(() => { listRef.current = listSelected; }, [listSelected]);
  useEffect(() => { addRef.current = addCustomers; }, [addCustomers]);
  useEffect(() => { removeRef.current = removeCustomers; }, [removeCustomers]);
  useEffect(() => { searchRef.current = searchCustomers; }, [searchCustomers]);

  // Mỗi chế độ có danh sách riêng: chỉ định → INCLUDE, ngoại trừ → EXCLUDE; "Tất cả khách" không dùng danh sách.
  const isExclude = scope === SKIP_PRODUCTION_SCOPE.EXCLUDED;
  const listType: SKIP_PRODUCTION_LIST_TYPE | null =
    scope === SKIP_PRODUCTION_SCOPE.SELECTED ? SKIP_PRODUCTION_LIST_TYPE.INCLUDE
      : isExclude ? SKIP_PRODUCTION_LIST_TYPE.EXCLUDE
        : null;

  useEffect(() => {
    setCustomers([]);
    if (!listType) return;
    (async () => {
      const res = await listRef.current(branchId, listType);
      if (res && isSuccess(res)) setCustomers((res.data as SkipProductionCustomer[]) || []);
    })();
  }, [branchId, listType]);

  // Tìm khách (debounce 350ms)
  useEffect(() => {
    const kw = keyword.trim();
    if (!kw) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      const res = await searchRef.current({ key_search: kw, limit: 20, status: 1 });
      setSearching(false);
      if (res && isSuccess(res)) setResults(((res.data as any)?.list || []) as Customer[]);
    }, 350);
    return () => clearTimeout(t);
  }, [keyword]);

  const applyResult = (res: any, okMessage: string) => {
    if (res && isSuccess(res)) {
      setCustomers((res.data as SkipProductionCustomer[]) || []);
      showToast({ message: okMessage, type: "success" });
    } else {
      showToast({ message: res?.message || "Cập nhật thất bại", type: "error" });
    }
  };

  const handleAdd = async (c: Customer) => {
    if (!listType) return;
    setBusy(true);
    const res = await addRef.current(branchId, listType, [Number(c.id)]);
    setBusy(false);
    applyResult(res, `Đã thêm ${c.name}`);
    setKeyword("");
    setResults([]);
  };

  const handleRemove = async (c: SkipProductionCustomer) => {
    if (!listType) return;
    setBusy(true);
    const res = await removeRef.current(branchId, listType, [Number(c.customer_id)]);
    setBusy(false);
    applyResult(res, `Đã bỏ ${c.customer_name}`);
  };

  const addedIds = new Set(customers.map((c) => Number(c.customer_id)));

  return (
    <div className="mt-4 ml-12 space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="font-semibold text-gray-600">Áp dụng cho:</span>
        {[
          { value: SKIP_PRODUCTION_SCOPE.ALL, label: "Tất cả khách" },
          { value: SKIP_PRODUCTION_SCOPE.EXCLUDED, label: "Tất cả, trừ khách được loại trừ" },
          { value: SKIP_PRODUCTION_SCOPE.SELECTED, label: "Chỉ khách được chỉ định" },
        ].map((opt) => (
          <label key={opt.value} className="flex items-center gap-2 cursor-pointer text-gray-700">
            <input
              type="radio"
              name={`skip-production-scope-${branchId}`}
              checked={scope === opt.value}
              disabled={disabled}
              onChange={() => onScopeChange(opt.value)}
              className="accent-green-600"
            />
            {opt.label}
          </label>
        ))}
      </div>

      {listType && (
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-3">
          <div className={`text-sm font-semibold ${isExclude ? "text-amber-700" : "text-green-700"}`}>
            {isExclude
              ? "Khách KHÔNG áp dụng — vẫn quét sọt/máy giặt như thường"
              : "Khách được áp dụng — bỏ qua quét sọt/máy giặt"}
          </div>
          <div className="relative">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Tìm khách theo tên / số điện thoại để thêm..."
              disabled={busy}
              className="w-full rounded-xl border border-gray-200 px-4 py-2 text-sm focus:border-green-500 focus:outline-none focus:ring-4 focus:ring-green-500/10"
            />
            {keyword.trim() && (
              <div className="absolute z-10 mt-1 w-full max-h-64 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg">
                {searching ? (
                  <div className="px-4 py-3 text-sm text-gray-400">Đang tìm...</div>
                ) : results.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-gray-400">Không tìm thấy khách</div>
                ) : (
                  results.map((c) => {
                    const added = addedIds.has(Number(c.id));
                    return (
                      <button
                        key={c.id}
                        type="button"
                        disabled={added || busy}
                        onClick={() => handleAdd(c)}
                        className="flex w-full items-center justify-between px-4 py-2 text-left text-sm hover:bg-green-50 disabled:cursor-default disabled:hover:bg-white"
                      >
                        <span className="text-gray-700">
                          {c.name} <span className="text-gray-400">· {c.phone}</span>
                        </span>
                        {added && <span className="text-xs text-green-600">Đã thêm</span>}
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {loadingList ? (
            <div className="text-sm text-gray-400">Đang tải danh sách khách...</div>
          ) : customers.length === 0 ? (
            isExclude ? (
              <div className="text-xs text-gray-400">
                Chưa loại trừ khách nào — hiện áp dụng cho tất cả khách của chi nhánh này.
              </div>
            ) : (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-700">
                Chưa có khách nào — hiện tính năng KHÔNG áp dụng cho khách nào của chi nhánh này.
              </div>
            )
          ) : (
            <div className="flex flex-wrap gap-2">
              {customers.map((c) => (
                <span
                  key={c.customer_id}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${
                    isExclude
                      ? "border-amber-200 bg-amber-50 text-amber-800"
                      : "border-green-200 bg-green-50 text-green-800"
                  }`}
                >
                  {c.customer_name}
                  {c.customer_phone && <span className="opacity-70">{c.customer_phone}</span>}
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => handleRemove(c)}
                    className="hover:text-red-600"
                    title="Bỏ khách này"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
          )}
          <p className="text-xs text-gray-400">
            Đơn điều chuyển xét theo danh sách của chi nhánh đang giặt.
          </p>
        </div>
      )}
    </div>
  );
};

export default SkipProductionScope;
